/**
 * Tree Transform Functions for Caster Mold
 * Transforms API responses to frontend tree data
 *
 * Hierarchy:
 *   Caster-Online
 *     Strand 1
 *       Mold #1 (online)
 *         Copper Mold Tube (pos 1)
 *         Mold Body (pos 2)
 *         Oil Distribution Gasket (pos 3)
 *         Level Control (pos 4)
 *         Oil Connector (pos 5)
 *         Water Jacket Flume (pos 6)
 *         Flanges (group pos 7) → children
 *         Collars (group pos 8) → children
 *         Seals (group pos 9) → children
 *         Foot Roll Left (pos 10) → children
 *         Foot Roll Right (pos 11) → children
 *         Foot Roll Internal (pos 12) → children
 *         Foot Roll External (pos 13) → children
 *     Strand 2
 *       Mold #2 (online) ...
 *     Strand 3 (empty)
 *     Strand 4 (empty)
 *     Strand 5 (empty)
 *   Caster-Offline
 *     Mold Spares
 *       Mold #3 (offline) ...
 *     Copper Mold Tube Spares
 *       spare parts...
 *     ... (other spares containers)
 */

import type { PartsTreeRowAPI } from "../api/types";
import type { PartInfo, EquipmentStatus } from "../../../../shared/models";
import type { CasterTreeRow } from "../types";
import {
  createContainerRow,
  buildSparePartsMap,
  sortAndDeduplicatePositions,
  parseExpectedChildPositions,
  type ExpectedChildPosition,
} from "../../../../shared/utils/tree-transform-helpers";
import {
  PART_TYPE_IDS,
  PART_IDS,
  CONTAINER_NAMES,
  SPARES_CONTAINER_NAMES,
  SPARES_CONTAINER_ORDER,
  STRAND_CONTAINER_IDS,
  isStrandContainer,
  isGroupType,
  isFootRollPosition,
  isMoldStandaloneChild,
  isMoldGroupChild,
} from "../constants/parts";

// PartsTreeRowAPI already includes all Caster-specific fields
// (WarehouseNumber, DrawingNumber, LastTask*, etc.)

// ============================================
// Types for Internal Use
// ============================================

interface MoldInfo {
  name: string;
  status: EquipmentStatus;
  parentId: number;
  campaignValue: number;
  totalValue: number;
  initialAbsoluteValue: number;
  positionInContainer: number;
  partTypeId: number;
  lastTaskTimestamp: Date | null;
  lastTaskId: number | null;
}

// ============================================
// Strand Name Helpers
// ============================================

/**
 * Build a map of strand PartId → display name from the raw API data.
 * Mold rows in the API have L1PartId = strand container and L1PartName = strand Part name.
 * We only need one row per strand since L1PartName is the same across all mold rows for a strand.
 */
const buildStrandNamesMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, string> => {
  const strandNamesMap = new Map<number, string>();

  for (const row of apiData) {
    if (
      row.L1PartTypeId === PART_TYPE_IDS.MOLD &&
      isStrandContainer(row.L1PartId ?? undefined) &&
      row.L1PartName &&
      !strandNamesMap.has(row.L1PartId!)
    ) {
      strandNamesMap.set(row.L1PartId!, row.L1PartName);
    }
  }

  return strandNamesMap;
};

/**
 * Look up a strand's display name from the transformed tree data.
 * Strand container rows have rowType="container" and ContainerId = strandPartId.
 *
 * @param treeData - Transformed CasterTreeRow[] from transformAPIToTreeData
 * @param strandId - The strand container PartId (1002-1006)
 * @returns The strand display name, or fallback "Strand {strandId}"
 */
export const getStrandName = (
  treeData: CasterTreeRow[],
  strandId: number,
): string =>
  treeData.find((r) => r.rowType === "container" && r.ContainerId === strandId)
    ?.ContainerName ?? `Strand ${strandId}`;

// ============================================
// Helper Functions
// ============================================

/**
 * Build PartInfo including WarehouseNumber and DrawingNumber
 */
const buildPartInfo = (row: PartsTreeRowAPI): PartInfo | null => {
  const hasPart = row.L2PartId !== null;
  if (!hasPart) return null;

  return {
    SerialNumber: row.SerialNumber || "",
    WarehouseNumber: row.WarehouseNumber || "",
    DrawingNumber: row.DrawingNumber || "",
  };
};

/**
 * Build info display string from part fields
 */
const buildInfoDisplay = (row: PartsTreeRowAPI): string | null => {
  if (row.L2PartId === null) return null;
  return (
    [row.WarehouseNumber, row.DrawingNumber].filter(Boolean).join(" ") || "-"
  );
};

/**
 * Build a map of Mold IDs to mold info from API data
 * Molds are parts under Strand containers (online) or Mold Spares (offline)
 */
const buildMoldInfoMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, MoldInfo> => {
  const moldInfoMap = new Map<number, MoldInfo>();

  for (const row of apiData) {
    // A mold row has L1PartTypeId = MOLD (1003) and L2PartId
    if (row.L1PartTypeId === PART_TYPE_IDS.MOLD && row.L2PartId) {
      const isOnline = isStrandContainer(row.L1PartId ?? undefined);
      moldInfoMap.set(row.L2PartId, {
        name: row.L2PartName || `Mold #${row.L2PartId}`,
        status: isOnline ? "Online" : "Offline",
        parentId: row.L1PartId ?? PART_IDS.MOLD_SPARES,
        campaignValue: row.CampaignValue || 0,
        totalValue: row.TotalValue || 0,
        initialAbsoluteValue: row.InitialAbsoluteValue || 0,
        positionInContainer: row.L1PositionId ?? 0,
        partTypeId: row.L1PartTypeId,
        lastTaskTimestamp: row.LastTaskTimestamp ?? null,
        lastTaskId: row.LastTaskId ?? null,
      });
    }
  }

  return moldInfoMap;
};

/**
 * Build a map of Mold IDs to their direct children (positions on the mold)
 * These are rows where the parent is a Mold instance
 */
const buildMoldPositionsMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, PartsTreeRowAPI[]> => {
  const map = new Map<number, PartsTreeRowAPI[]>();

  for (const row of apiData) {
    // Position rows have L1PartId as the Mold partId
    // and L1PartTypeId in the component range (1020+) or group range (1010+)
    // Skip container/structure types
    if (
      row.L1PartId !== null &&
      row.L1PartTypeId !== null &&
      row.L1PartTypeId >= PART_TYPE_IDS.FLANGES &&
      row.L1PositionId !== null
    ) {
      const positions = map.get(row.L1PartId);
      if (positions) {
        positions.push(row);
      } else {
        map.set(row.L1PartId, [row]);
      }
    }
  }

  return map;
};

/**
 * Build a map of group fixture PartId -> sub-component positions
 * Groups (Flanges, Collars, Seals, Foot Roll Assembly) have child positions
 */
const buildGroupSubComponentsMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, PartsTreeRowAPI[]> => {
  const map = new Map<number, PartsTreeRowAPI[]>();

  for (const row of apiData) {
    if (
      row.L1PartId !== null &&
      row.L1PartTypeId !== null &&
      row.L1PositionId !== null &&
      // Sub-components have PartTypeIds in the component ranges (1030-1065)
      row.L1PartTypeId >= PART_TYPE_IDS.UPPER_FLANGE &&
      row.L1PartTypeId <= PART_TYPE_IDS.SPRAY_NOZZLE
    ) {
      const parentId = row.L1PartId;
      const subComponents = map.get(parentId);
      if (subComponents) {
        subComponents.push(row);
      } else {
        map.set(parentId, [row]);
      }
    }
  }

  return map;
};

/**
 * Build a map from strand PartId → mold PartId installed in that strand.
 * Also returns a list of offline mold IDs (in Mold Spares).
 */
const classifyMoldsByLocation = (
  moldInfoMap: Map<number, MoldInfo>,
): {
  strandMoldsMap: Map<number, number>;
  offlineMolds: number[];
} => {
  const strandMoldsMap = new Map<number, number>();
  const offlineMolds: number[] = [];

  for (const [moldId, info] of moldInfoMap) {
    if (info.status === "Online" && isStrandContainer(info.parentId)) {
      strandMoldsMap.set(info.parentId, moldId);
    } else {
      offlineMolds.push(moldId);
    }
  }

  return { strandMoldsMap, offlineMolds };
};

// ============================================
// Row Factory Functions
// ============================================

/**
 * Create a mold equipment row
 */
const createMoldRow = (
  moldId: number,
  moldInfo: MoldInfo,
  basePath: string[],
  strandPartId?: number,
): CasterTreeRow => ({
  id: `equipment-${moldId}`,
  path: [...basePath, moldInfo.name],
  rowType: "equipment",
  EquipmentId: moldId,
  EquipmentName: moldInfo.name,
  Status: moldInfo.status,
  PositionId: moldInfo.positionInContainer,
  PositionName: moldInfo.name,
  PartTypeId: PART_TYPE_IDS.MOLD,
  L2PartId: moldId,
  L2PartName: moldInfo.name,
  HasAssignedPart: true,
  PartInfo: null,
  PartStatus: "Installed",
  Heats: moldInfo.campaignValue,
  HeatsSinceRepair: 0,
  TotalHeats: moldInfo.totalValue,
  InitialAbsoluteValue: moldInfo.initialAbsoluteValue,
  StatusCode: "O",
  InstalledDate: moldInfo.lastTaskTimestamp,
  LastTaskId: moldInfo.lastTaskId,
  Info: null,
  Comments: null,
  HasChildren: true,
  StrandPartId: strandPartId,
});

/**
 * Create a standalone location row (positions 1-6 on Mold)
 * These are direct replaceable components (Copper Mold Tube, Mold Body, etc.)
 */
const createStandaloneLocationRow = (
  moldId: number,
  moldInfo: MoldInfo,
  position: PartsTreeRowAPI,
  moldPath: string[],
): CasterTreeRow => {
  const positionId = position.L1PositionId ?? 0;
  const hasPart = position.L2PartId !== null;
  const partInfo = buildPartInfo(position);

  return {
    id: `location-${moldId}-${positionId}`,
    path: [...moldPath, position.L1PositionName ?? `Position ${positionId}`],
    rowType: "location",
    EquipmentId: moldId,
    EquipmentName: moldInfo.name,
    Status: moldInfo.status,
    PositionId: positionId,
    PositionName: position.L1PositionName ?? `Position ${positionId}`,
    PartTypeId: position.L1PartTypeId,
    L2PartId: hasPart ? position.L2PartId : null,
    L2PartName: hasPart ? position.L2PartName : null,
    HasAssignedPart: hasPart,
    PartInfo: partInfo,
    PartStatus: hasPart ? "Installed" : "Empty",
    Heats: hasPart ? (position.CampaignValue ?? null) : null,
    HeatsSinceRepair: hasPart ? (position.PartialValue ?? null) : null,
    TotalHeats: position.TotalValue || 0,
    InitialAbsoluteValue: position.InitialAbsoluteValue || 0,
    StatusCode: position.StatusCode,
    InstalledDate: position.LastTaskTimestamp ?? null,
    LastTaskId: position.LastTaskId ?? null,
    Info: buildInfoDisplay(position),
    Comments: position.Comments,
    HasChildren: false,
    IsReplaceable: position.IsReplaceable ?? undefined,
    IsArray: position.IsArray ?? undefined,
    SortOrder: position.SortOrder ?? undefined,
  };
};

/**
 * Create a group container row (Flanges, Collars, Seals at positions 7-9)
 * These are non-replaceable assemblies that hold child components
 */
const createGroupLocationRow = (
  moldId: number,
  moldInfo: MoldInfo,
  position: PartsTreeRowAPI,
  moldPath: string[],
): CasterTreeRow => {
  const positionId = position.L1PositionId ?? 0;
  // Groups have a fixture installed (the group assembly itself)
  const groupFixtureInstalled =
    isGroupType(position.L1PartTypeId) && position.L2PartId !== null;

  return {
    id: `location-${moldId}-${positionId}`,
    path: [...moldPath, position.L1PositionName ?? `Position ${positionId}`],
    rowType: "location",
    EquipmentId: moldId,
    EquipmentName: moldInfo.name,
    Status: moldInfo.status,
    PositionId: positionId,
    PositionName: position.L1PositionName ?? `Position ${positionId}`,
    PartTypeId: position.L1PartTypeId,
    // Keep actual PartId for backend operations (Set Initial Metric → Apply to Children)
    L2PartId: position.L2PartId ?? null,
    L2PartName: null,
    HasAssignedPart: false,
    PartInfo: null,
    PartStatus: "Empty",
    Heats: position.CampaignValue ?? null,
    HeatsSinceRepair: position.PartialValue ?? null,
    TotalHeats: position.TotalValue || 0,
    InitialAbsoluteValue: position.InitialAbsoluteValue || 0,
    StatusCode: position.StatusCode,
    InstalledDate: position.LastTaskTimestamp ?? null,
    LastTaskId: position.LastTaskId ?? null,
    Info: null,
    Comments: position.Comments,
    HasChildren: true,
    IsReplaceable: position.IsReplaceable ?? undefined,
    IsArray: position.IsArray ?? undefined,
    SortOrder: position.SortOrder ?? undefined,
    // Store group fixture PartId for sub-component API calls
    ParentGroupPartTypeId: groupFixtureInstalled
      ? (position.L1PartTypeId ?? undefined)
      : undefined,
  };
};

/**
 * Create a group sub-component row (child of Flanges, Collars, or Seals)
 */
const createGroupSubComponentRow = (
  moldId: number,
  moldInfo: MoldInfo,
  groupFixturePartId: number,
  parentPositionId: number,
  childPosition: ExpectedChildPosition,
  parentPath: string[],
  apiPosition?: PartsTreeRowAPI,
): CasterTreeRow => {
  const hasPart =
    apiPosition?.L2PartId !== null && apiPosition?.L2PartId !== undefined;

  const partInfo: PartInfo | null =
    hasPart && apiPosition
      ? {
          SerialNumber: apiPosition.SerialNumber || "",
          WarehouseNumber: apiPosition.WarehouseNumber || "",
          DrawingNumber: apiPosition.DrawingNumber || "",
        }
      : null;

  return {
    id: `location-${moldId}-${parentPositionId}-${childPosition.positionId}`,
    path: [...parentPath, childPosition.positionName],
    rowType: "location",
    EquipmentId: moldId,
    EquipmentName: moldInfo.name,
    Status: moldInfo.status,
    PositionId: childPosition.positionId,
    PositionName: childPosition.positionName,
    PartTypeId: childPosition.partTypeId,
    L2PartId: hasPart && apiPosition ? apiPosition.L2PartId : null,
    L2PartName: hasPart && apiPosition ? apiPosition.L2PartName : null,
    HasAssignedPart: hasPart,
    PartInfo: partInfo,
    PartStatus: hasPart ? "Installed" : "Empty",
    Heats: hasPart ? (apiPosition?.CampaignValue ?? null) : null,
    HeatsSinceRepair: hasPart ? (apiPosition?.PartialValue ?? null) : null,
    TotalHeats: apiPosition?.TotalValue || 0,
    InitialAbsoluteValue: apiPosition?.InitialAbsoluteValue || 0,
    StatusCode: apiPosition?.StatusCode ?? "B",
    InstalledDate: apiPosition?.LastTaskTimestamp ?? null,
    LastTaskId: apiPosition?.LastTaskId ?? null,
    Info:
      hasPart && apiPosition
        ? [apiPosition.WarehouseNumber, apiPosition.DrawingNumber]
            .filter(Boolean)
            .join(" ") || "-"
        : null,
    Comments: apiPosition?.Comments ?? null,
    HasChildren: false,
    IsReplaceable: apiPosition?.IsReplaceable ?? undefined,
    IsArray: apiPosition?.IsArray ?? undefined,
    SortOrder: apiPosition?.SortOrder ?? undefined,
    ParentGroupPartTypeId:
      childPosition.partTypeId >= PART_TYPE_IDS.UPPER_FLANGE &&
      childPosition.partTypeId <= PART_TYPE_IDS.OIL_CONNECTOR_ORING
        ? getParentGroupTypeId(childPosition.partTypeId)
        : undefined,
  };
};

/**
 * Create a Foot Roll Assembly position row (positions 10-13 on Mold)
 * Each has sub-components: Foot Roll, Roll Shaft, Roller Support, Shaft Keeper, Z1 Spray, Spray Nozzle
 */
const createFootRollPositionRow = (
  moldId: number,
  moldInfo: MoldInfo,
  position: PartsTreeRowAPI,
  moldPath: string[],
): CasterTreeRow => {
  const positionId = position.L1PositionId ?? 0;
  const fixtureInstalled = position.L2PartId !== null;

  return {
    id: `location-${moldId}-${positionId}`,
    path: [...moldPath, position.L1PositionName ?? `Position ${positionId}`],
    rowType: "location",
    EquipmentId: moldId,
    EquipmentName: moldInfo.name,
    Status: moldInfo.status,
    PositionId: positionId,
    PositionName: position.L1PositionName ?? `Position ${positionId}`,
    PartTypeId: position.L1PartTypeId,
    // Keep actual PartId for backend operations (Set Initial Metric → Apply to Children)
    L2PartId: position.L2PartId ?? null,
    L2PartName: null,
    HasAssignedPart: false,
    PartInfo: null,
    PartStatus: "Empty",
    Heats: position.CampaignValue ?? null,
    HeatsSinceRepair: position.PartialValue ?? null,
    TotalHeats: position.TotalValue || 0,
    InitialAbsoluteValue: position.InitialAbsoluteValue || 0,
    StatusCode: position.StatusCode,
    InstalledDate: position.LastTaskTimestamp ?? null,
    LastTaskId: position.LastTaskId ?? null,
    Info: null,
    Comments: position.Comments,
    HasChildren: true,
    IsReplaceable: position.IsReplaceable ?? undefined,
    IsArray: position.IsArray ?? undefined,
    SortOrder: position.SortOrder ?? undefined,
    ParentGroupPartTypeId: fixtureInstalled
      ? PART_TYPE_IDS.FOOT_ROLL_ASSEMBLY
      : undefined,
  };
};

/**
 * Create a Foot Roll sub-component row
 */
const createFootRollSubComponentRow = (
  moldId: number,
  moldInfo: MoldInfo,
  assemblyFixturePartId: number,
  footRollPositionId: number,
  childPosition: ExpectedChildPosition,
  parentPath: string[],
  apiPosition?: PartsTreeRowAPI,
): CasterTreeRow => {
  const hasPart =
    apiPosition?.L2PartId !== null && apiPosition?.L2PartId !== undefined;

  const partInfo: PartInfo | null =
    hasPart && apiPosition
      ? {
          SerialNumber: apiPosition.SerialNumber || "",
          WarehouseNumber: apiPosition.WarehouseNumber || "",
          DrawingNumber: apiPosition.DrawingNumber || "",
        }
      : null;

  return {
    id: `location-${moldId}-${footRollPositionId}-${childPosition.positionId}`,
    path: [...parentPath, childPosition.positionName],
    rowType: "location",
    EquipmentId: moldId,
    EquipmentName: moldInfo.name,
    Status: moldInfo.status,
    PositionId: childPosition.positionId,
    PositionName: childPosition.positionName,
    PartTypeId: childPosition.partTypeId,
    L2PartId: hasPart && apiPosition ? apiPosition.L2PartId : null,
    L2PartName: hasPart && apiPosition ? apiPosition.L2PartName : null,
    HasAssignedPart: hasPart,
    PartInfo: partInfo,
    PartStatus: hasPart ? "Installed" : "Empty",
    Heats: hasPart ? (apiPosition?.CampaignValue ?? null) : null,
    HeatsSinceRepair: hasPart ? (apiPosition?.PartialValue ?? null) : null,
    TotalHeats: apiPosition?.TotalValue || 0,
    InitialAbsoluteValue: apiPosition?.InitialAbsoluteValue || 0,
    StatusCode: apiPosition?.StatusCode ?? "B",
    InstalledDate: apiPosition?.LastTaskTimestamp ?? null,
    LastTaskId: apiPosition?.LastTaskId ?? null,
    Info:
      hasPart && apiPosition
        ? [apiPosition.WarehouseNumber, apiPosition.DrawingNumber]
            .filter(Boolean)
            .join(" ") || "-"
        : null,
    Comments: apiPosition?.Comments ?? null,
    HasChildren: false,
    IsReplaceable: apiPosition?.IsReplaceable ?? undefined,
    IsArray: apiPosition?.IsArray ?? undefined,
    SortOrder: apiPosition?.SortOrder ?? undefined,
    ParentGroupPartTypeId: PART_TYPE_IDS.FOOT_ROLL_ASSEMBLY,
    FootRollPositionId: footRollPositionId,
  };
};

/**
 * Create a spare part row
 */
const createSparePartRow = (
  containerId: number,
  containerName: string,
  part: PartsTreeRowAPI,
  containerPath: string[],
): CasterTreeRow => {
  const partInfo: PartInfo = {
    SerialNumber: part.SerialNumber || "",
    WarehouseNumber: part.WarehouseNumber || "",
    DrawingNumber: part.DrawingNumber || "",
  };

  const partDisplayName =
    [part.SerialNumber, part.WarehouseNumber, part.DrawingNumber]
      .filter(Boolean)
      .join(" - ") || "-";

  // Append PartId to make path unique
  const uniquePathName = `${partDisplayName} (#${part.L2PartId})`;

  return {
    id: `spare-${containerId}-${part.L2PartId}`,
    path: [...containerPath, uniquePathName],
    rowType: "sparePart",
    ContainerId: containerId,
    ContainerName: containerName,
    EquipmentId: containerId,
    L2PartId: part.L2PartId!,
    L2PartName: part.L2PartName,
    PartInfo: partInfo,
    PartTypeId: part.L1PartTypeId,
    TypeIdentity: part.IAm
      ? part.IAm.split(",").map(Number)
      : [part.L1PartTypeId],
    Heats: part.CampaignValue ?? null,
    HeatsSinceRepair: part.PartialValue ?? null,
    TotalHeats: part.TotalValue || 0,
    InitialAbsoluteValue: part.InitialAbsoluteValue || 0,
    StatusCode: part.StatusCode,
    InstalledDate: part.LastTaskTimestamp ?? null,
    LastTaskId: part.LastTaskId ?? null,
    HasAssignedPart: true,
    IsReplaceable: part.IsReplaceable ?? undefined,
    IsArray: part.IsArray ?? undefined,
    SortOrder: part.SortOrder ?? undefined,
  };
};

/**
 * Determine the parent group PartTypeId for a given component PartTypeId
 */
const getParentGroupTypeId = (
  componentPartTypeId: number,
): number | undefined => {
  // Flange components (1030-1034)
  if (
    componentPartTypeId >= PART_TYPE_IDS.UPPER_FLANGE &&
    componentPartTypeId <= PART_TYPE_IDS.WATER_JACKET_FIXING_FLANGE
  ) {
    return PART_TYPE_IDS.FLANGES;
  }
  // Collar components (1040-1041)
  if (
    componentPartTypeId >= PART_TYPE_IDS.UPPER_COLLAR &&
    componentPartTypeId <= PART_TYPE_IDS.LOWER_COLLAR
  ) {
    return PART_TYPE_IDS.COLLARS;
  }
  // Seal components (1050-1055)
  if (
    componentPartTypeId >= PART_TYPE_IDS.UPPER_SEAL &&
    componentPartTypeId <= PART_TYPE_IDS.OIL_CONNECTOR_ORING
  ) {
    return PART_TYPE_IDS.SEALS;
  }
  // Foot Roll components (1060-1065)
  if (
    componentPartTypeId >= PART_TYPE_IDS.FOOT_ROLL &&
    componentPartTypeId <= PART_TYPE_IDS.SPRAY_NOZZLE
  ) {
    return PART_TYPE_IDS.FOOT_ROLL_ASSEMBLY;
  }
  return undefined;
};

// ============================================
// Composition Functions
// ============================================

/**
 * Add a mold and all its positions to the result array
 */
const addMoldWithPositions = (
  result: CasterTreeRow[],
  moldId: number,
  moldInfo: MoldInfo,
  basePath: string[],
  moldPositionsMap: Map<number, PartsTreeRowAPI[]>,
  groupSubComponentsMap: Map<number, PartsTreeRowAPI[]>,
  strandPartId?: number,
): void => {
  const moldPath = [...basePath, moldInfo.name];

  // Create the mold equipment row
  result.push(createMoldRow(moldId, moldInfo, basePath, strandPartId));

  // Get all positions for this mold
  const positions = moldPositionsMap.get(moldId) || [];

  // Sort and deduplicate positions
  const sortedPositions = sortAndDeduplicatePositions(positions);

  for (const pos of sortedPositions) {
    const positionId = pos.L1PositionId ?? 0;

    // Standalone components (positions 1-6)
    if (isMoldStandaloneChild(positionId)) {
      result.push(createStandaloneLocationRow(moldId, moldInfo, pos, moldPath));
      continue;
    }

    // Group containers (positions 7-9: Flanges, Collars, Seals)
    if (isMoldGroupChild(positionId)) {
      const groupRow = createGroupLocationRow(moldId, moldInfo, pos, moldPath);
      result.push(groupRow);

      // Add sub-components if group fixture is installed
      const hasGroupFixture =
        isGroupType(pos.L1PartTypeId) && pos.L2PartId !== null;

      if (hasGroupFixture) {
        addGroupSubComponents(
          result,
          moldId,
          moldInfo,
          pos,
          moldPath,
          groupSubComponentsMap,
        );
      }
      continue;
    }

    // Foot Roll Assembly positions (10-13)
    if (isFootRollPosition(positionId)) {
      const footRollRow = createFootRollPositionRow(
        moldId,
        moldInfo,
        pos,
        moldPath,
      );
      result.push(footRollRow);

      // Add sub-components if assembly fixture is installed
      const hasFixture = pos.L2PartId !== null;

      if (hasFixture) {
        addFootRollSubComponents(
          result,
          moldId,
          moldInfo,
          pos,
          moldPath,
          groupSubComponentsMap,
        );
      }
    }
  }
};

/**
 * Add sub-components for a group container (Flanges, Collars, Seals)
 */
const addGroupSubComponents = (
  result: CasterTreeRow[],
  moldId: number,
  moldInfo: MoldInfo,
  groupPosition: PartsTreeRowAPI,
  moldPath: string[],
  groupSubComponentsMap: Map<number, PartsTreeRowAPI[]>,
): void => {
  const childPositions = parseExpectedChildPositions(
    groupPosition.ExpectedChildPositionIds,
    groupPosition.ExpectedChildPositionNames,
    groupPosition.ExpectedChildPartTypeIds,
    groupPosition.ExpectedChildPartTypeNames,
  );

  if (childPositions.length === 0) return;

  const groupPath = [
    ...moldPath,
    groupPosition.L1PositionName ?? `Position ${groupPosition.L1PositionId}`,
  ];

  const groupFixturePartId = groupPosition.L2PartId!;
  const actualSubComponents =
    groupSubComponentsMap.get(groupFixturePartId) || [];

  for (const childPos of childPositions) {
    const apiPosition = actualSubComponents.find(
      (sc) => sc.L1PositionId === childPos.positionId,
    );

    result.push(
      createGroupSubComponentRow(
        moldId,
        moldInfo,
        groupFixturePartId,
        groupPosition.L1PositionId ?? 0,
        childPos,
        groupPath,
        apiPosition,
      ),
    );
  }
};

/**
 * Add sub-components for a Foot Roll Assembly position
 */
const addFootRollSubComponents = (
  result: CasterTreeRow[],
  moldId: number,
  moldInfo: MoldInfo,
  footRollPosition: PartsTreeRowAPI,
  moldPath: string[],
  groupSubComponentsMap: Map<number, PartsTreeRowAPI[]>,
): void => {
  const childPositions = parseExpectedChildPositions(
    footRollPosition.ExpectedChildPositionIds,
    footRollPosition.ExpectedChildPositionNames,
    footRollPosition.ExpectedChildPartTypeIds,
    footRollPosition.ExpectedChildPartTypeNames,
  );

  if (childPositions.length === 0) return;

  const footRollPath = [
    ...moldPath,
    footRollPosition.L1PositionName ??
      `Position ${footRollPosition.L1PositionId}`,
  ];

  const assemblyFixturePartId = footRollPosition.L2PartId!;
  const actualSubComponents =
    groupSubComponentsMap.get(assemblyFixturePartId) || [];
  const footRollPositionId = footRollPosition.L1PositionId ?? 0;

  for (const childPos of childPositions) {
    const apiPosition = actualSubComponents.find(
      (sc) => sc.L1PositionId === childPos.positionId,
    );

    result.push(
      createFootRollSubComponentRow(
        moldId,
        moldInfo,
        assemblyFixturePartId,
        footRollPositionId,
        childPos,
        footRollPath,
        apiPosition,
      ),
    );
  }
};

/**
 * Add spares containers and their spare parts to the result array
 */
const addSparesContainers = (
  result: CasterTreeRow[],
  offlinePath: string[],
  sparePartsMap: Map<number, PartsTreeRowAPI[]>,
): void => {
  // Skip Mold Spares — offline molds are handled separately as equipment rows
  // with full hierarchy (via addMoldWithPositions), not as simple spare parts.
  const componentSparesOrder = SPARES_CONTAINER_ORDER.filter(
    (id) => id !== PART_IDS.MOLD_SPARES,
  );

  for (const containerId of componentSparesOrder) {
    const containerName = SPARES_CONTAINER_NAMES[containerId];
    if (!containerName) continue;
    const containerPath = [...offlinePath, containerName];

    result.push(
      createContainerRow<CasterTreeRow>(
        containerId,
        containerName,
        containerPath,
        "sparesContainer",
      ),
    );

    const spareParts = sparePartsMap.get(containerId) || [];
    for (const part of spareParts) {
      if (!part.L2PartId) continue;
      result.push(
        createSparePartRow(containerId, containerName, part, containerPath),
      );
    }
  }
};

// ============================================
// Main Transform Function
// ============================================

/**
 * Create a strand container row with mold installation status
 */
const createStrandContainerRow = (
  strandPartId: number,
  strandName: string,
  path: string[],
  hasMoldInstalled: boolean,
): CasterTreeRow => ({
  ...createContainerRow<CasterTreeRow>(
    strandPartId,
    strandName,
    path,
    "container",
  ),
  HasMoldInstalled: hasMoldInstalled,
});

/**
 * Transform API response from GetPartsTree (per strand) + GetCasterOfflinePartsTree to frontend CasterTreeRow[]
 *
 * Creates full hierarchy:
 * - Caster-Online
 *   - Strand 1 → Mold #X (or empty)
 *   - Strand 2 → Mold #Y (or empty)
 *   - Strand 3 (empty)
 *   - Strand 4 (empty)
 *   - Strand 5 (empty)
 * - Caster-Offline
 *   - Mold Spares → offline molds with full hierarchy
 *   - Component Spares → spare parts
 *
 * @param apiData - Raw rows from EQP.GetPartsTree (5 strands) + EQP.GetCasterOfflinePartsTree
 * @returns Flattened tree data for ag-Grid
 */
export const transformAPIToTreeData = (
  apiData: PartsTreeRowAPI[],
): CasterTreeRow[] => {
  const result: CasterTreeRow[] = [];
  const casterData = apiData as PartsTreeRowAPI[];

  // Step 1: Build lookup maps
  const moldInfoMap = buildMoldInfoMap(casterData);
  const moldPositionsMap = buildMoldPositionsMap(casterData);
  const groupSubComponentsMap = buildGroupSubComponentsMap(casterData);
  const sparesContainerIds = Object.keys(SPARES_CONTAINER_NAMES).map(Number);
  const sparePartsMap = buildSparePartsMap(casterData, sparesContainerIds);
  const strandNamesMap = buildStrandNamesMap(casterData);

  // Step 2: Classify molds by location (strand or offline)
  const { strandMoldsMap, offlineMolds } = classifyMoldsByLocation(moldInfoMap);

  // ==========================================
  // BUILD THE HIERARCHY
  // ==========================================

  // 1. Caster-Online container
  const casterOnlinePath = [CONTAINER_NAMES.CASTER_ONLINE];
  result.push(
    createContainerRow<CasterTreeRow>(
      PART_IDS.CASTER_ONLINE,
      CONTAINER_NAMES.CASTER_ONLINE,
      casterOnlinePath,
      "container",
    ),
  );

  // 1a. Strand containers (5 strands, each with 0 or 1 mold)
  for (const strandPartId of STRAND_CONTAINER_IDS) {
    const strandName =
      strandNamesMap.get(strandPartId) ?? `Strand ${strandPartId}`;
    const strandPath = [...casterOnlinePath, strandName];
    const moldId = strandMoldsMap.get(strandPartId);
    const hasMoldInstalled = moldId !== undefined;

    result.push(
      createStrandContainerRow(
        strandPartId,
        strandName,
        strandPath,
        hasMoldInstalled,
      ),
    );

    // Add the mold and its positions if one is installed in this strand
    if (moldId !== undefined) {
      const moldInfo = moldInfoMap.get(moldId);
      if (moldInfo) {
        addMoldWithPositions(
          result,
          moldId,
          moldInfo,
          strandPath,
          moldPositionsMap,
          groupSubComponentsMap,
          strandPartId,
        );
      }
    }
  }

  // 2. Caster-Offline container
  const casterOfflinePath = [CONTAINER_NAMES.CASTER_OFFLINE];
  result.push(
    createContainerRow<CasterTreeRow>(
      PART_IDS.CASTER_OFFLINE,
      CONTAINER_NAMES.CASTER_OFFLINE,
      casterOfflinePath,
      "container",
    ),
  );

  // 2a. Mold Spares container (contains offline molds with full hierarchy)
  const moldSparesPath = [...casterOfflinePath, CONTAINER_NAMES.MOLD_SPARES];
  result.push(
    createContainerRow<CasterTreeRow>(
      PART_IDS.MOLD_SPARES,
      CONTAINER_NAMES.MOLD_SPARES,
      moldSparesPath,
      "sparesContainer",
    ),
  );

  // Add offline molds under Mold Spares (full hierarchy loaded upfront)
  for (const moldId of offlineMolds) {
    const moldInfo = moldInfoMap.get(moldId);
    if (!moldInfo) continue;

    addMoldWithPositions(
      result,
      moldId,
      moldInfo,
      moldSparesPath,
      moldPositionsMap,
      groupSubComponentsMap,
    );
  }

  // 2b. Component spares containers (skip Mold Spares, already added above)
  addSparesContainers(result, casterOfflinePath, sparePartsMap);

  return result;
};
